import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { User, LogOut } from 'lucide-react';
import { ROLES } from '../../constants/roles';
import { Link } from 'react-router-dom';

interface HeaderProps {
  userRole: string;
}

const normalizeRole = (role: string) => {
  const r = role?.toLowerCase();

  if (r === "superadmin" || r === "super_admin") return ROLES.SUPERADMIN;
  if (r === "consultant") return "CONSULTANT";
  if (r === "junior_doctor" || r === "jrdoctor" || r === "jr_doctor") return ROLES.DOCTOR;

  return r?.toUpperCase();
};

const Header: React.FC<HeaderProps> = ({ userRole }) => {
  const { user, logout } = useAuth();  

  // Normalize incoming role (backend/mock)
  const role = normalizeRole(userRole);

  const getNavigationItems = () => {
    switch (role) {
      case ROLES.DOCTOR:
        return [
          { name: "Dashboard", href: "/dashboard" },
          { name: "Patients", href: "/patients" },
          { name: "MDT Reports", href: "/reports" },
        ];

      case ROLES.SUPERADMIN:
        return [
          { name: "Dashboard", href: "/dashboard" },
          { name: "Doctors", href: "/doctors" },
          { name: "Users", href: "/users" },
          // { name: "Patients", href: "/patients" },
          { name: "MDT Reports", href: "/reports" },
        ];

      default:
        return [];
    }
  };

  const navigationItems = getNavigationItems();

  return (
    <header className="bg-[#F8FAFA] shadow-sm border-b border-neutral-200 w-full fixed top-0 left-0 right-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 w-full">

          {/* Logo + Navigation */}
          <div className="flex w-full justify-between items-center space-x-4 px-4">
            <div className="flex-shrink-0">
              <img
                src="/logo/DiagnomIQ.png"
                alt="AI Patient Care Platform"
                className="mx-auto my-2 h-12 w-25 object-contain"
              />
            </div>

            <nav className="hidden md:flex space-x-8">
              {navigationItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.href}
                  className="text-neutral-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4 min-w-0">
            <div className="relative group min-w-0">
              <button className="flex items-center space-x-2 text-neutral-700 hover:text-primary-600 transition-colors duration-200 min-w-0">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-primary-600" />
                </div>

                <span
                  className="hidden md:block text-sm font-medium max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap"
                  title={user?.name ?? "User"}
                >
                  {user?.name ?? "User"}
                </span>
              </button>

              <div
                className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 
                           opacity-0 invisible group-hover:opacity-100 group-hover:visible 
                           transition-all duration-200 border border-neutral-200"
              >
                <Link
                  to="/profile"
                  className="block px-4 py-2 text-sm text-neutral-700 border-b border-neutral-100 hover:bg-neutral-50 transition-colors duration-200"
                >
                  <div
                    className="font-medium overflow-hidden text-ellipsis whitespace-nowrap"
                    title={user?.name ?? "User"}
                  >
                    {user?.name ?? "User"}
                  </div>

                  <div
                    className="text-neutral-500 capitalize overflow-hidden text-ellipsis whitespace-nowrap"
                    title={(role ?? "").replace("_", " ")}
                  >
                    {(role ?? "").replace("_", " ")}
                  </div>
                </Link>

                <button
                  onClick={logout}
                  className="flex items-center w-full px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors duration-200"
                >
                  <LogOut className="h-4 w-4 mr-2 flex-shrink-0" />
                  Sign out
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </header>
  );
};

export default Header;
